import type { HTMLAttributes, ReactNode } from "react";
import type { Bucket } from "../api/types";
import { bucketStyle } from "../theme";
import { cx } from "./cx";
import styles from "./Badge.module.css";

export type BadgeTone =
  | "neutral"
  | "accent"
  | "success"
  | "warning"
  | "danger"
  | "info";

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
  /**
   * Seau du grand livre. Teinte le badge de la couleur du seau — c'est ce
   * rappel, identique sur le dashboard, dans l'historique et dans les tuiles,
   * qui fait comprendre le modèle. Prioritaire sur `tone`.
   */
  bucket?: Bucket;
  /** Ton hors grand livre : statut de mission, avertissement, information. */
  tone?: BadgeTone;
  /** Défaut `md`. */
  size?: "sm" | "md";
  /** Ajoute une pastille de couleur avant le texte. */
  dot?: boolean;
  children: ReactNode;
};

/**
 * Étiquette courte : un seau, un statut de mission, une nature d'écriture.
 *
 * Le texte doit rester lisible seul : un badge n'est jamais la seule porteuse
 * d'une information, puisque sa couleur ne se lit pas en noir et blanc.
 */
export function Badge({
  bucket,
  tone = "neutral",
  size = "md",
  dot = false,
  className,
  style,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cx(
        styles.badge,
        size === "sm" && styles.sm,
        !bucket && tone !== "neutral" && styles[tone],
        className,
      )}
      style={{ ...(bucket ? bucketStyle(bucket) : {}), ...style }}
      {...rest}
    >
      {dot && <span className={styles.pastille} aria-hidden="true" />}
      {children}
    </span>
  );
}
